import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr, formatDate } from "@/lib/api";
import { PageHeader, StatusChip } from "@/components/Bits";
import UploadOverlay from "@/components/UploadOverlay";
import BulkUploadDialog from "@/components/BulkUploadDialog";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { UploadSimple, CaretDown, X, Stack } from "@phosphor-icons/react";
import CameraCaptureButton from "@/components/CameraCaptureButton";
import { useAuth } from "@/contexts/AuthContext";

export default function PurchaseList() {
    const { user } = useAuth();
    const canPost = ["founder", "accountant"].includes(user?.role);
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [docTypeFilter, setDocTypeFilter] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [busy, setBusy] = useState(false);
    const [uploadName, setUploadName] = useState("");
    const [uploadKind, setUploadKind] = useState("invoice");
    const [menuOpen, setMenuOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const nav = useNavigate();

    const load = async () => {
        try {
            const params = {};
            if (status) params.status = status;
            if (docTypeFilter !== "all") params.doc_type = docTypeFilter;
            const { data } = await api.get("/purchase-invoices", { params });
            setItems(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { load(); }, [status, docTypeFilter]);

    // Preload category ledgers (direct-cost + indirect-expense groups) for the bulk dialog
    useEffect(() => {
        (async () => {
            try {
                const [ledgersRes, groupsRes] = await Promise.all([
                    api.get("/ledgers"), api.get("/account-groups"),
                ]);
                const grpBy = new Map(groupsRes.data.map((g) => [g.id, g]));
                setCategories(
                    ledgersRes.data
                        .filter((l) => {
                            const g = grpBy.get(l.group_id);
                            return g && (g.category === "direct_cost" || g.category === "indirect_expenses");
                        })
                        .map((l) => ({ ...l, group_name: grpBy.get(l.group_id)?.name }))
                );
            } catch { /* silent */ }
        })();
    }, []);

    // Client-side date-range filter + tally when active.
    const dateFilterActive = !!(dateFrom || dateTo);
    // Sort state — default oldest-first (invoice_date asc) so drafts flow
    // chronologically for the reviewer.
    const [sortCol, setSortCol] = useState("invoice_date");
    const [sortDir, setSortDir] = useState("asc");
    const toggleSort = (col) => {
        if (sortCol === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else { setSortCol(col); setSortDir(col === "invoice_date" ? "asc" : "desc"); }
    };
    const filtered = useMemo(() => {
        let rows = items;
        if (dateFilterActive) {
            rows = rows.filter((p) => {
                const d = (p.invoice_date || p.created_at || "").slice(0, 10);
                if (dateFrom && d < dateFrom) return false;
                if (dateTo && d > dateTo) return false;
                return true;
            });
        }
        // Sort
        const dir = sortDir === "asc" ? 1 : -1;
        const key = (r) => {
            switch (sortCol) {
                case "invoice_date": return (r.invoice_date || r.created_at || "");
                case "vendor":       return (r.vendor_name || "").toLowerCase();
                case "invoice_no":   return (r.invoice_no || "").toLowerCase();
                case "subtotal":     return Number(r.subtotal) || 0;
                case "gst_amount":   return Number(r.gst_amount) || 0;
                case "total":        return Number(r.total) || 0;
                case "status":       return r.status || "";
                case "posted_by":    return (r.posted_by_name || r.created_by_name || "").toLowerCase();
                default:             return 0;
            }
        };
        return [...rows].sort((a, b) => {
            const ka = key(a), kb = key(b);
            if (ka < kb) return -1 * dir;
            if (ka > kb) return 1 * dir;
            return 0;
        });
    }, [items, dateFrom, dateTo, dateFilterActive, sortCol, sortDir]);
    const tally = useMemo(() => {
        if (!dateFilterActive) return null;
        const sub = filtered.reduce((s, p) => s + (Number(p.subtotal) || 0) * (p.doc_type === "debit_note" ? -1 : 1), 0);
        const gst = filtered.reduce((s, p) => s + (Number(p.gst_amount) || 0) * (p.doc_type === "debit_note" ? -1 : 1), 0);
        const total = filtered.reduce((s, p) => s + (Number(p.total) || 0) * (p.doc_type === "debit_note" ? -1 : 1), 0);
        return { count: filtered.length, sub, gst, total };
    }, [filtered, dateFilterActive]);

    const upload = async (file, docType = "invoice") => {
        if (!file) return;
        setBusy(true);
        setUploadName(file.name);
        setUploadKind(docType);
        try {
            const fd = new FormData(); fd.append("file", file);
            const { data } = await api.post("/purchase-invoices/extract", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                params: { doc_type: docType },
            });
            toast.success(docType === "debit_note" ? "Debit note uploaded — AI is reading it" : "Uploaded — AI is reading your invoice");
            nav(`/purchase/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); setUploadName(""); }
    };

    return (
        <>
            <Toaster richColors position="top-right" />
            <UploadOverlay open={busy} filename={uploadName} kind={uploadKind === "debit_note" ? "debit note" : "purchase"} />
            <PageHeader
                title="Purchases"
                sub="Purchase invoices and debit notes. Upload photo/PDF, AI extracts, you confirm."
                right={
                    <div className="flex items-center gap-2">
                        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                               placeholder="From" title="From date"
                               className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                               data-testid="pl-date-from" />
                        <span className="text-xs text-stone-400">to</span>
                        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                               placeholder="To" title="To date"
                               className="text-xs border rounded-sm px-2 py-1 bg-white h-8"
                               data-testid="pl-date-to" />
                        {(dateFrom || dateTo) ? (
                            <button type="button" onClick={() => { setDateFrom(""); setDateTo(""); }}
                                    className="text-stone-400 hover:text-stone-700 px-1"
                                    title="Clear dates" data-testid="pl-date-clear">
                                <X size={13} />
                            </button>
                        ) : null}
                        <div className="inline-flex rounded-sm border" style={{ borderColor: "var(--se-border)" }} data-testid="purchase-doctype-filter">
                            {[["all", "All"], ["invoice", "Invoices"], ["debit_note", "Debit notes"]].map(([k, label]) => (
                                <button
                                    key={k}
                                    onClick={() => setDocTypeFilter(k)}
                                    data-testid={`filter-${k}`}
                                    className={`px-3 py-1.5 text-xs ${docTypeFilter === k ? "text-white" : "text-stone-600"}`}
                                    style={{ background: docTypeFilter === k ? "var(--se-brand)" : "transparent" }}
                                >{label}</button>
                            ))}
                        </div>
                        {canPost ? (
                            <button
                                onClick={() => setStatus(status === "draft" ? "" : "draft")}
                                data-testid="purchase-pending-review-btn"
                                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-sm border ${status === "draft" ? "text-white" : "text-amber-800"}`}
                                style={{
                                    background: status === "draft" ? "#b45309" : "#fef3c7",
                                    borderColor: status === "draft" ? "#b45309" : "#fcd34d",
                                }}
                                title="Team-uploaded invoices waiting for you to review and post"
                            >
                                Pending my review
                                <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-white/25">
                                    {items.filter((i) => i.status === "draft").length}
                                </span>
                            </button>
                        ) : null}
                        <select value={status} onChange={(e) => setStatus(e.target.value)} className="text-xs border rounded-sm px-2 py-1 bg-white" data-testid="filter-status">
                            <option value="">Any status</option>
                            <option value="draft">Draft</option>
                            <option value="posted">Posted</option>
                            <option value="cancelled">Cancelled</option>
                            <option value="reversed">Reversed</option>
                        </select>
                        <CameraCaptureButton
                            onFile={(f) => upload(f, "invoice")}
                            label="Camera"
                            testid="purchase-camera-btn"
                            disabled={busy}
                        />
                        <button
                            onClick={() => setBulkOpen(true)}
                            disabled={busy}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs border rounded-sm text-purple-800 bg-purple-50 hover:bg-purple-100 ${busy ? "opacity-50 pointer-events-none" : ""}`}
                            style={{ borderColor: "#c4b5fd" }}
                            title="Upload many invoices in one go — all pinned to a single expense category"
                            data-testid="purchase-bulk-upload-btn"
                        >
                            <Stack size={14} /> Bulk upload
                        </button>
                        <div className="relative">
                            <button
                                onClick={() => setMenuOpen((v) => !v)}
                                disabled={busy}
                                className={`inline-flex items-center gap-2 px-4 py-2 text-sm text-white se-btn-primary ${busy ? "opacity-50 pointer-events-none" : ""}`}
                                data-testid="purchase-upload-btn"
                            >
                                <UploadSimple size={16} /> {busy ? "Uploading…" : "Upload"} <CaretDown size={12} />
                            </button>
                            {menuOpen ? (
                                <div className="absolute right-0 top-full mt-1 bg-white border rounded-sm shadow-lg z-50 min-w-[180px]" style={{ borderColor: "var(--se-border)" }}>
                                    <label className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer" data-testid="upload-invoice">
                                        <input type="file" accept="application/pdf,image/*" className="hidden"
                                               onChange={(e) => { setMenuOpen(false); upload(e.target.files?.[0], "invoice"); }} />
                                        Purchase invoice
                                    </label>
                                    <label className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-50 cursor-pointer border-t" style={{ borderColor: "var(--se-border)" }} data-testid="upload-debit-note">
                                        <input type="file" accept="application/pdf,image/*" className="hidden"
                                               onChange={(e) => { setMenuOpen(false); upload(e.target.files?.[0], "debit_note"); }} />
                                        Debit note
                                    </label>
                                </div>
                            ) : null}
                        </div>
                    </div>
                }
            />
            <div className="p-8">
                {tally ? (
                    <div className="bg-white border rounded-md px-4 py-3 mb-4 flex items-center gap-6 text-xs" style={{ borderColor: "var(--se-border)" }} data-testid="pl-tally">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Rows</div>
                            <div className="font-mono text-sm mt-0.5">{tally.count}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Taxable</div>
                            <div className="font-mono text-sm mt-0.5">₹ {inr(tally.sub)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">GST</div>
                            <div className="font-mono text-sm mt-0.5">₹ {inr(tally.gst)}</div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-stone-500">Total</div>
                            <div className="font-mono text-sm mt-0.5 font-semibold">₹ {inr(tally.total)}</div>
                        </div>
                        <div className="ml-auto text-[10px] text-stone-500">
                            Debit notes are subtracted. Range: {dateFrom || "start"} → {dateTo || "today"}.
                        </div>
                    </div>
                ) : null}
                <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: "var(--se-border)" }}>
                    <table className="se-table" data-testid="purchase-table">
                        <thead>
                            <tr>
                                <SortTh col="invoice_date" label="Date" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                                <th>Type</th>
                                <SortTh col="vendor" label="Vendor" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                                <SortTh col="invoice_no" label="No." sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                                <SortTh col="subtotal" label="Subtotal" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                                <SortTh col="gst_amount" label="GST" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                                <SortTh col="total" label="Total" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} num />
                                <SortTh col="status" label="Status" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                                <SortTh col="posted_by" label="Posted" sortCol={sortCol} sortDir={sortDir} onClick={toggleSort} />
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? <tr><td colSpan="9" className="text-stone-500 py-8 text-center">Nothing here yet. Upload your first invoice.</td></tr> :
                                filtered.map((p) => {
                                    const isDN = p.doc_type === "debit_note";
                                    const isPetty = p.source_type === "petty_cash";
                                    return (
                                        <tr key={p.id} className="cursor-pointer"
                                            onClick={() => nav(isPetty ? "/petty-cash" : `/purchase/${p.id}`)}
                                            data-testid={`row-${p.id}`}>
                                            <td>{formatDate(p.invoice_date || p.created_at)}</td>
                                            <td>
                                                {isDN ? (
                                                    <span className="se-chip se-chip-warn text-[10px]">debit note</span>
                                                ) : isPetty ? (
                                                    <span className="se-chip se-chip-teal text-[10px]" title="Paid via Petty Cash — click to view in Petty Cash">petty cash</span>
                                                ) : (
                                                    <span className="text-xs text-stone-500">invoice</span>
                                                )}
                                            </td>
                                            <td>
                                                <div className="font-medium">{p.vendor_name || "—"}</div>
                                                <div className="text-xs text-stone-500">{p.vendor_gstin || ""}</div>
                                            </td>
                                            <td>{p.invoice_no || "—"}{p.linked_invoice_no ? <div className="text-[10px] text-stone-400">→ {p.linked_invoice_no}</div> : null}</td>
                                            <td className="se-num">₹ {inr(p.subtotal)}</td>
                                            <td className="se-num">₹ {inr(p.gst_amount)}</td>
                                            <td className="se-num font-medium" style={{ color: isDN ? "#9f1239" : undefined }}>
                                                {isDN ? "-" : ""}₹ {inr(p.total)}
                                            </td>
                                            <td><StatusChip status={p.status} /></td>
                                            <td className="text-[11px]">
                                                {p.status === "posted" && p.posted_by_name ? (
                                                    <>
                                                        <div>{p.posted_by_name}</div>
                                                        <div className="text-[10px] text-stone-400">{formatDate(p.posted_at)}</div>
                                                    </>
                                                ) : p.status === "draft" && p.created_by_name ? (
                                                    <>
                                                        <div className="text-amber-700 italic">uploaded by</div>
                                                        <div className="text-stone-500">{p.created_by_name}</div>
                                                    </>
                                                ) : (
                                                    <span className="text-stone-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            }
                        </tbody>
                    </table>
                </div>
            </div>
            <BulkUploadDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
                                kind="purchase" categories={categories}
                                onAllDone={load} />
        </>
    );
}


function SortTh({ col, label, sortCol, sortDir, onClick, num }) {
    const active = sortCol === col;
    return (
        <th className={num ? "text-right" : ""}>
            <button
                onClick={() => onClick(col)}
                className={`inline-flex items-center gap-1 select-none hover:text-purple-700 transition-colors ${num ? "flex-row-reverse" : ""}`}
                style={{ color: active ? "#7c3aed" : "inherit", fontWeight: active ? 600 : "inherit" }}
                data-testid={`sort-${col}`}
            >
                {label}
                <span className="text-[9px] opacity-60">
                    {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
                </span>
            </button>
        </th>
    );
}

